import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import App from './App.tsx';

import './styles/components/token.css';
import './index.css';
import '@dhl-official/tokens/index.css';

/**
 * Global Handler for Vite Dynamic Import Failures (Chunk Load Errors)
 *
 * WHY THIS EXISTS:
 * When a new deployment occurs, old code chunks (split JS files with unique hashes)
 * are removed from the server. If a user tries to navigate to a lazy-loaded route
 * after a deploy, the browser will request an outdated file hash and throw a
 * TypeError: "Failed to fetch dynamically imported module".
 *
 * HOW IT HEALS:
 * This listener catches Vite's custom `vite:preloadError` event and forces a hard
 * browser reload to pull down the latest compiled application code from the server.
 *
 * INFINITE LOOP PROTECTION:
 * Uses a `sessionStorage` flag to guarantee the application only attempts ONE automatic
 * recovery reload. If the error persists (due to persistent network dropouts or broken
 * assets), it bails out to prevent trapping the user in an infinite refresh loop.
 *
 * Note: The 'vite-preload-reloaded' flag is safely cleared inside the root
 * App component's useEffect hook once the application successfully mounts.
 */
window.addEventListener('vite:preloadError', () => {
    const reloadedKey = 'vite-preload-reloaded';

    if (sessionStorage.getItem(reloadedKey)) {
        console.error('Vite preload failed twice consecutively. Stopping reload loop.');
        return;
    }
    // Set the recovery flag and force-refresh the page to fetch the latest assets
    sessionStorage.setItem(reloadedKey, '1');
    window.location.reload();
});

const registerDuilElements = () => {
    void import('@dhl-official/stencil-library/loader')
        .then(({ defineCustomElements }) => defineCustomElements())
        .catch((error: unknown) => {
            console.error('Failed to register DHL UI elements:', error);
        });
};

const bootstrapApplication = () => {
    document.documentElement.setAttribute('data-dui-brand-refresh-ui-enabled', 'true');

    createRoot(document.getElementById('root')!).render(
        <StrictMode>
            <App />
        </StrictMode>,
    );

    // Let the first render start before loading the full DUIL element registry.
    window.setTimeout(registerDuilElements, 0);
};

bootstrapApplication();
